import {stringify} from 'qs';
import {createSelector} from 'reselect';

import {selectors as dateFilterSelectors, keys} from '@stubhub/react-date-filter';
import {parseHomeRecoAPIPartnerConfig} from '@stubhub/react-partner-helper';
import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';

import {getScreenSize} from '../../helpers/screen-device-helper';
import {
  hideCbtEventsSelector,
  isCBTModuleEnabledSelector,
  isRedirectModalEnabledSelector,
} from '../../store/gsConfig/FF/selectors';
import {CMA_DEFAULTS, EXTERNAL_SOURCE_IDS, CATEGORY_IDS, SEARCH_CACHE_MAX_AGE} from '../../utils/constants';
import DateTimeFormat from '../../utils/datetimeformat';
import {ConfigMapping} from '../react-entity-list/config-mapping';

const ACTION_TYPES = {
  CITY_PAGE_LOADING: 'CITY_PAGE_LOADING',
  CITY_PAGE_LOADED: 'CITY_PAGE_LOADED',
  CITY_PAGE_RESET: 'CITY_PAGE_RESET',
  CITY_PAGE_SPORT_LOADING: 'CITY_PAGE_SPORT_LOADING',
  CITY_PAGE_SPORT_LOADED: 'CITY_PAGE_SPORT_LOADED',
  CITY_PAGE_THEATER_LOADING: 'CITY_PAGE_THEATER_LOADING',
  CITY_PAGE_THEATER_LOADED: 'CITY_PAGE_THEATER_LOADED',
  CITY_PAGE_CONCERT_LOADING: 'CITY_PAGE_CONCERT_LOADING',
  CITY_PAGE_CONCERT_LOADED: 'CITY_PAGE_CONCERT_LOADED',
};
const namespace = 'app.city';
const viewportSelector = (state) => state.viewport;
const screenSizeSelector = createSelector(viewportSelector, (viewport = {}) => viewport.screenSize);
const deviceSelector = createSelector(
  (state) => state.device,
  (device = {}) => device.type,
);

/**
 * Get config for entity list component
 * @param {objet} props
 */
const getGenreConfigForEntityList = (props) => {
  const {screenSize, enableGenrePerformerCardMweb, enableGenrePerformerCardDesktop} = props;
  const type = enableGenrePerformerCardMweb || enableGenrePerformerCardDesktop ? 'performer' : 'event';
  const config = ConfigMapping[type][screenSize];

  return config || {};
};

/**
 * Load events for each category.
 * If category id is in props, that indicates only specified category need to fetch new events / reset events, so just handle this category.
 * @param {object} props
 * @param {object} options options that need to be taken care of
 * @param {object} options.toggles related toggles
 * @param {boolean} options.toggles.isDateFilterEnabled whether to enable date filter / empty state on city page
 * @param {boolean} options.resetSports whether to reset sport events
 * @param {boolean} options.resetConcerts whether to reset concert events
 * @param {boolean} options.resetTheater whether to reset theater events
 */
const loadEvents = (props, {toggles = {}, resetSports, resetConcerts, resetTheater}) => {
  return (dispatch, getState) => {
    dispatch({type: ACTION_TYPES.CITY_PAGE_LOADING, isLoading: true});
    const {externalSourceId, parentGeos, entityName, lat, lng} = props;

    // Check whether sport / theater is needed
    let isConcertNeeded = false;
    let isSportNeeded = false;
    let isTheaterNeeded = false;
    if (props.sports || props.concerts || props.theater) {
      // If sub genre name are specified, then request related events
      isSportNeeded = props.sports && props.sports.name;
      isTheaterNeeded = props.theater && props.theater.name;
      isConcertNeeded = props.concerts && props.concerts.name;
    } else {
      // If no sub genre name specified, then request all events
      isSportNeeded = true;
      isTheaterNeeded = true;
      isConcertNeeded = true;
    }

    const STATE = getState();
    const {lang} = STATE;
    const hideCbtEvents = hideCbtEventsSelector(STATE);
    const searchQueryParams = {
      shstore: STATE.gsConfig.shstoreId,
      sourceId: -1,
      ir: true,
      units: 'mi',
      parking: false,
      lang: true,
      highValueProfileIndicator: false,
      eventType: 'main',
      sort: 'popularity desc',
    };

    if (externalSourceId !== EXTERNAL_SOURCE_IDS.STUBHUB_SEO) {
      const point = `${lat},${lng}`;
      searchQueryParams.geoId = hideCbtEvents ? CMA_DEFAULTS.GEO_ID : undefined;
      searchQueryParams.point = point;
      searchQueryParams.radius = 50;
      searchQueryParams.geoExpansion = false;
    } else {
      const parentGeoId = parentGeos[1] ? parentGeos[1].id : 0;
      searchQueryParams.geoId = hideCbtEvents ? CMA_DEFAULTS.GEO_ID : parentGeoId;
      searchQueryParams.city = entityName;
    }

    // Process date filter range
    let eventStartDate = dateFilterSelectors.startDate(STATE);
    const eventEndDate = dateFilterSelectors.endDate(STATE);
    const dateKey = dateFilterSelectors.key(STATE);
    eventStartDate = toggles.isDateFilterEnabled && eventStartDate ? DateTimeFormat.toAPIString(eventStartDate) : '';
    let endDate = eventEndDate ? new Date(eventEndDate.getTime()) : null;
    if (dateKey === keys.K_CHOOSE && endDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    endDate = toggles.isDateFilterEnabled && endDate ? DateTimeFormat.toAPIString(endDate) : '';
    // Translate the catetory type name to correct api parameter
    let concertType = 'categoryId';
    let sportsType = 'categoryId';
    let theaterType = 'categoryId';
    sportsType = props.sports && getGenreType(props.sports.type);
    theaterType = props.theater && getGenreType(props.theater.type);
    concertType = props.concerts && getGenreType(props.concerts.type);
    let sportPromise;
    if (isSportNeeded) {
      dispatch({
        type: ACTION_TYPES.CITY_PAGE_SPORT_LOADING,
        isSportLoading: true,
        loadMoreSports: props.loadMoreSports === true,
      });
      sportPromise = loadSportEvents(searchQueryParams, props.sportEvents, sportsType, props.sports.ids, resetSports, {
        eventStartDate,
        eventEndDate: endDate,
        lang,
        config: getGenreConfigForEntityList(props),
      }).then((body) => {
        const {totalCount = 0, impressionToken} = body;
        const {data, offset, hasMore} = processEventGroupingDataByCategory(body, props.sportEvents.offset, resetSports);
        let sportData = resetSports ? [] : props.sportEvents.data || [];

        /**
         * Append the Event Data
         *
         */
        sportData = sportData.concat(data);
        const newSportEvents = {
          totalCount,
          offset,
          data: sportData,
          impressionToken,
          resetPage: resetSports,
          hasMore,
        };

        dispatch({
          type: ACTION_TYPES.CITY_PAGE_SPORT_LOADED,
          isSportLoading: false,
          loadMoreSports: true,
          sportEvents: newSportEvents,
        });

        return newSportEvents;
      });
    }

    let theaterPromise;
    if (isTheaterNeeded) {
      dispatch({
        type: ACTION_TYPES.CITY_PAGE_THEATER_LOADING,
        isTheaterLoading: true,
        loadMoreTheater: props.loadMoreTheater === true,
      });
      theaterPromise = loadTheaterEvents(
        searchQueryParams,
        props.theaterEvents,
        theaterType,
        props.theater.ids,
        resetTheater,
        {eventStartDate, eventEndDate: endDate, lang, config: getGenreConfigForEntityList(props)},
      ).then((body) => {
        const {totalCount = 0, impressionToken} = body;
        const {data, offset, hasMore} = processEventGroupingDataByCategory(
          body,
          props.theaterEvents.offset,
          resetTheater,
        );
        let theaterData = resetTheater ? [] : props.theaterEvents.data || [];

        /**
         * Append the Event Data
         *
         */
        theaterData = theaterData.concat(data);
        const newTheaterEvents = {
          totalCount,
          offset,
          data: theaterData,
          impressionToken,
          resetPage: resetTheater,
          hasMore,
        };

        dispatch({
          type: ACTION_TYPES.CITY_PAGE_THEATER_LOADED,
          isTheaterLoading: false,
          loadMoreTheater: true,
          theaterEvents: newTheaterEvents,
        });

        return newTheaterEvents;
      });
    }

    let concertPromise;
    if (isConcertNeeded) {
      dispatch({
        type: ACTION_TYPES.CITY_PAGE_CONCERT_LOADING,
        isConcertLoading: true,
        loadMoreConcerts: props.loadMoreConcerts === true,
      });
      concertPromise = loadConcertEvents(searchQueryParams, concertType, props.concerts.ids, {
        eventStartDate,
        eventEndDate: endDate,
        lang,
      }).then((res) => {
        const venues = getVenues(res.events) || /* istanbul ignore next */ [];
        dispatch({
          type: ACTION_TYPES.CITY_PAGE_CONCERT_LOADED,
          isConcertLoading: false,
          concertEvents: {data: venues, resetPage: resetConcerts},
        });

        return venues;
      });
    }

    const promiseList = [];
    isSportNeeded && promiseList.push(sportPromise);
    isTheaterNeeded && promiseList.push(theaterPromise);
    isConcertNeeded && promiseList.push(concertPromise);

    let isDateFilterChanged = true;
    if (!isSportNeeded || !isTheaterNeeded || !isConcertNeeded) {
      isDateFilterChanged = false;
    }

    return Promise.all(promiseList).then(() => {
      dispatch({type: ACTION_TYPES.CITY_PAGE_LOADED, isLoading: false, isDateFilterChanged});

      return null;
    });
  };
};

const genreTypeList = {
  category: 'categoryId',
  grouping: 'groupingId',
  performer: 'performerId',
};

const getGenreType = (type) => {
  return genreTypeList[type];
};

const loadSportEvents = (
  searchParams = {},
  sportEvents = {},
  type = 'categoryId',
  ids = [CATEGORY_IDS.SPORTS],
  resetSports,
  {eventStartDate, eventEndDate, lang, config},
) => {
  // Translate the ids arrary to api parameter
  const searchOptions = {...searchParams, [type]: ids.join(' |')};
  searchOptions.start = resetSports ? 0 : sportEvents.offset || 0;
  searchOptions.rows = config.stacked ? config.stacked.initialFetchCount : 4;

  if (eventStartDate && eventEndDate) {
    searchOptions.eventStartDate = eventStartDate;
    searchOptions.eventEndDate = eventEndDate;
  }

  return sendRequest('/search/catalog/events/v3/eventGroupings', searchOptions, {lang});
};

const loadTheaterEvents = (
  searchParams = {},
  theaterEvents = {},
  type = 'categoryId',
  ids = [CATEGORY_IDS.THEATER],
  resetTheater,
  {eventStartDate, eventEndDate, lang, config},
) => {
  // Translate the ids arrary to api parameter
  const searchOptions = {...searchParams, [type]: ids.join(' |')};
  searchOptions.start = resetTheater ? 0 : theaterEvents.offset || 0;
  searchOptions.rows = config.stacked ? config.stacked.initialFetchCount : 4;

  if (eventStartDate && eventEndDate) {
    searchOptions.eventStartDate = eventStartDate;
    searchOptions.eventEndDate = eventEndDate;
  }

  return sendRequest('/search/catalog/events/v3/eventGroupings', searchOptions, {lang});
};

const loadConcertEvents = (searchParams = {}, type = 'categoryId', ids = [1], {eventStartDate, eventEndDate, lang}) => {
  // Translate the ids arrary to api parameter
  const searchOptions = {...searchParams, [type]: ids.join(' |')};
  searchOptions.start = 0;
  searchOptions.rows = 100;

  if (eventStartDate && eventEndDate) {
    searchOptions.dateLocal = `${eventStartDate} TO ${eventEndDate}`;
  }

  return sendRequest('/search/catalog/events/v3', searchOptions, {lang});
};

const sendRequest = (path, params, {lang}) => {
  const cache = true;
  const cacheMaxAge = SEARCH_CACHE_MAX_AGE;

  return get({
    host: process.env.REACT_APP_API_HOST,
    path: `${path}?${params ? stringify(params) : ''}`,
    headers: {
      Authorization: process.env.REACT_APP_API_SECRET,
      'Accept-Language': lang,
    },
    json: true,
    cache,
    cacheMaxAge,
  });
};

const processEventGroupingDataByCategory = (eventData, prevOffset, reset) => {
  const data = parseHomeRecoAPIPartnerConfig(eventData.groups);
  const newData = processEventData(data);
  let offset = reset ? 0 : prevOffset || 0;
  offset += newData.length;
  const hasMore = offset < eventData.totalCount;

  return {
    data: newData,
    hasMore,
    offset,
  };
};

const processEventData = (data) => {
  const performers = data;

  const events = [];
  for (let i = 0; i < performers.length; i++) {
    const performer = performers[i];
    const performerEvents = performer.events;
    /* istanbul ignore else */
    if (performerEvents && performerEvents.length > 0) {
      const [event] = performerEvents;

      /*
       * We meed to pass additional information from the parent
       * down for tracking. create link to parent
       */
      event.__parent = performer;

      // Add JSONLD required data
      event.categories = (performer.ancestors || {}).categories || [];
      event.performersCollection = [performer];
      event.eventDateLocal = event.dateLocal;

      events.push(event);
    }
  }

  return events;
};

function resetCityPage() {
  return (dispatch) => {
    dispatch({type: ACTION_TYPES.CITY_PAGE_RESET});
  };
}

const controller = new Controller({
  namespace,
  mapStateToProps(state) {
    const localState = this.getLocalState(state);

    return {
      deviceType: deviceSelector(state),
      screenSize: getScreenSize(screenSizeSelector(state), state.device),
      eventStartDate: dateFilterSelectors.startDate(state),
      eventEndDate: dateFilterSelectors.endDate(state),
      dateFilter: dateFilterSelectors.dateFilter(state) || {},
      isLoading: localState.isLoading !== false,
      isSportLoading: localState.isSportLoading !== false,
      isTheaterLoading: localState.isTheaterLoading !== false,
      isConcertLoading: localState.isConcertLoading !== false,
      sportsLoadMore: localState.loadMoreSports,
      theaterLoadMore: localState.loadMoreTheater,
      concertsLoadMore: localState.loadMoreConcerts,
      sportEvents: localState.sportEvents || {},
      theaterEvents: localState.theaterEvents || {},
      concertEvents: localState.concertEvents || {},
      isDateFilterChanged: localState.isDateFilterChanged,
      dateKey: dateFilterSelectors.key(state),
      isCBTModuleEnabled: isCBTModuleEnabledSelector(state),
      isRedirectModalEnabled: isRedirectModalEnabledSelector(state),
    };
  },
  actionCreators: {
    resetCityPage,
    loadEvents,
  },
  reducers: [
    ACTION_TYPES.CITY_PAGE_LOADING,
    ACTION_TYPES.CITY_PAGE_LOADED,
    ACTION_TYPES.CITY_PAGE_SPORT_LOADING,
    ACTION_TYPES.CITY_PAGE_SPORT_LOADED,
    ACTION_TYPES.CITY_PAGE_THEATER_LOADING,
    ACTION_TYPES.CITY_PAGE_THEATER_LOADED,
    ACTION_TYPES.CITY_PAGE_CONCERT_LOADING,
    ACTION_TYPES.CITY_PAGE_CONCERT_LOADED,
  ],
});

controller.addResetReducer([ACTION_TYPES.CITY_PAGE_RESET]);

export function getVenues(/* istanbul ignore next */ events = []) {
  const venueIdsObj = events.reduce((acc, event) => {
    const {venue, id: venueId, name: venueName} = event;
    if (!acc[venueId]) {
      acc[venueId] = {
        count: 1,
        events: [event],
        venueId,
        venueName,
        venue,
      };
    } else {
      acc[venueId].count = ++acc[venueId].count;
      acc[venueId].events.push(event);
    }

    return acc;
  }, {});

  // Convert the venueIdsObj to venues array: [{count: 1, venueId: 11, venueName: 'xx venue name', venue: {}, events: [{}, {}]}]
  const venues = Object.values(venueIdsObj);

  /*
   * Sort the venueIds according to the logic below:
   * the venueId which has hot event count at most comes the first, but if hot event count is same, then compare the venue name according to alpha beta asc
   */
  venues.sort((a, b) => {
    let ret = b.count - a.count;
    if (ret === 0) {
      /* istanbul ignore else */
      if (a.venueName < b.venueName) {
        ret = -1;
      } else if (a.venueName > b.venueName) {
        ret = 1;
      }
    }

    return ret;
  });

  return venues;
}

export default controller;
